import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readItemsFamiliesUser } from "../itemsFamiliesUi";
import useCatalogAccess from "../useCatalogAccess";
import useItemDialogFocus from "../useItemDialogFocus";
import RecipeComposerView from "./RecipeComposerView";
import { getUnitName } from "../../utils/unitNames";
import { recipeComposerText } from "./recipeComposerMessages";
import "../../PurchasingUi.css";
import "./RecipeComposer.css";
import {
  formatRecipeCurrency,
  formatRecipeNumber,
  parseRecipeNarrative,
  serializeRecipeNarrative,
} from "../recipesUi";

const buildEmptyForm = () => ({
  name: "",
  code: "",
  recipe_type: "finished",
  photo_url: "",
  is_inventoriable: false,
  produced_unit_id: "",
  yield_quantity: "1",
  description: "",
  preparation_steps: "",
  internal_notes: "",
  gallery_urls: [""],
  narrative_source: null,
  lines: [],
  equivalent_lines: [],
});

const INGREDIENT_DRAG_TYPE = "application/x-pilcore-ingredient";

const EMPTY_INGREDIENT_PICKER = {
  query: "",
  family_id: "",
  subfamily_id: "",
  category_id: "",
  subcategory_id: "",
  selected_item_id: "",
};

const buildBlankLine = (seed = {}) => ({
  family_id: seed.family_id || "",
  subfamily_id: seed.subfamily_id || "",
  category_id: seed.category_id || "",
  subcategory_id: seed.subcategory_id || "",
  family_name: seed.family_name || "",
  subfamily_name: seed.subfamily_name || "",
  category_name: seed.category_name || "",
  subcategory_name: seed.subcategory_name || "",
  item_id: seed.item_id || "",
  item_name: seed.item_name || "",
  item_code: seed.item_code || "",
  supplier_code: seed.supplier_code || "",
  item_description: seed.item_description || "",
  supplier_names: Array.isArray(seed.supplier_names) ? seed.supplier_names : [],
  unit: seed.unit,
  subunit: seed.subunit,
  subunit2: seed.subunit2,
  pack_size: seed.pack_size ?? null,
  subunit2_size: seed.subunit2_size ?? null,
  unit_cost: seed.unit_cost == null ? null : Number(seed.unit_cost),
  unit_level: seed.unit_level || "unit",
  input_mode: seed.input_mode || "quantity",
  value: seed.value ?? "",
  is_confirmed: seed.is_confirmed ?? false,
});

function sortByName(rows) {
  return [...(rows || [])].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
      sensitivity: "base",
    })
  );
}

function uniqueById(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    if (!row?.id) return;
    if (!map.has(String(row.id))) {
      map.set(String(row.id), row);
    }
  });
  return [...map.values()];
}

function getHierarchyObjectsFromItem(item) {
  const family = item?.subcategory?.category?.subfamily?.family || item?.category?.subfamily?.family;
  const subfamily = item?.subcategory?.category?.subfamily || item?.category?.subfamily;
  const category = item?.subcategory?.category || item?.category;
  const subcategory = item?.subcategory || null;

  return { family, subfamily, category, subcategory };
}

function deriveHierarchyFromItem(item) {
  const { family, subfamily, category, subcategory } = getHierarchyObjectsFromItem(item);

  return {
    family_id: family?.id ? String(family.id) : "",
    subfamily_id: subfamily?.id ? String(subfamily.id) : "",
    category_id: category?.id ? String(category.id) : "",
    subcategory_id: subcategory?.id ? String(subcategory.id) : "",
    family_name: family?.name || "",
    subfamily_name: subfamily?.name || "",
    category_name: category?.name || "",
    subcategory_name: subcategory?.name || "",
  };
}

function mergeItemRecords(catalogItem = {}, fullItem = {}) {
  const merged = { ...(catalogItem || {}), ...(fullItem || {}) };

  return {
    ...merged,
    category: catalogItem?.category || fullItem?.category || merged.category,
    subcategory: catalogItem?.subcategory || fullItem?.subcategory || merged.subcategory,
    item_suppliers: catalogItem?.item_suppliers || fullItem?.item_suppliers || [],
    unit: fullItem?.unit || catalogItem?.unit || merged.unit,
    subunit: fullItem?.subunit || catalogItem?.subunit || merged.subunit,
    subunit2: fullItem?.subunit2 || catalogItem?.subunit2 || merged.subunit2,
  };
}

function getSupplierNames(item) {
  const names = (item?.item_suppliers || [])
    .map((relation) => relation?.supplier?.name)
    .filter(Boolean);

  return [...new Set(names)];
}

function getSupplierLabel(source) {
  const names = Array.isArray(source?.supplier_names) ? source.supplier_names : getSupplierNames(source);
  if (!names.length) return "No supplier linked";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function getHierarchyPathFromLine(line) {
  return [line.family_name, line.subfamily_name, line.category_name, line.subcategory_name]
    .filter(Boolean)
    .join(" / ");
}

function getProductSearchText(item) {
  const hierarchy = deriveHierarchyFromItem(item);
  return [
    item?.name,
    item?.code,
    item?.supplier_code,
    item?.description,
    hierarchy.family_name,
    hierarchy.subfamily_name,
    hierarchy.category_name,
    hierarchy.subcategory_name,
    ...getSupplierNames(item),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function matchesProductQuery(item, query) {
  const terms = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!terms.length) return true;
  const searchText = getProductSearchText(item);
  return terms.every((term) => searchText.includes(term));
}

function getLineUnitOptions(line, languageId) {
  return ["unit", "subunit", "subunit2"].map((value) => ({
    value, label: getUnitName(line[value], languageId) || (value === "unit" ? recipeComposerText(languageId, "Main unit") : ""),
  })).filter((option) => option.label);
}

function buildLineFromItemRecord(item, unitCost = 0, seed = {}) {
  const hierarchy = deriveHierarchyFromItem(item);

  return buildBlankLine({
    ...hierarchy,
    item_id: item?.id ? String(item.id) : seed.item_id || "",
    item_name: item?.name || seed.item_name || "",
    item_code: item?.code || "",
    supplier_code: item?.supplier_code || "",
    item_description: item?.description || "",
    supplier_names: getSupplierNames(item),
    unit: item?.unit,
    subunit: item?.subunit,
    subunit2: item?.subunit2,
    pack_size: item?.pack_size ?? null,
    subunit2_size: item?.subunit2_size ?? null,
    unit_cost: unitCost == null ? null : Number(unitCost),
    unit_level: seed.unit_level || "unit",
    input_mode: seed.input_mode || "quantity",
    value: seed.value ?? "",
    is_confirmed: seed.is_confirmed ?? false,
  });
}

function getItemPackSummary(item, languageId = "en") {
  const unitLabel = getUnitName(item?.unit, languageId) || recipeComposerText(languageId, "Main unit");
  const subunitLabel = getUnitName(item?.subunit, languageId);
  const subunit2Label = getUnitName(item?.subunit2, languageId);
  const packSize = Number(item?.pack_size || 0);
  const subunit2Size = Number(item?.subunit2_size || 0);

  if (packSize && subunitLabel && subunit2Size && subunit2Label) {
    return `${formatRecipeNumber(packSize, 2, languageId)} ${subunitLabel} x ${formatRecipeNumber(
      subunit2Size,
      2, languageId
    )} ${subunit2Label}`;
  }

  if (packSize && subunitLabel) {
    return `${formatRecipeNumber(packSize, 2, languageId)} ${subunitLabel} / ${unitLabel}`;
  }

  return unitLabel;
}

function getLineQuantitySummary(line, languageId = "en") {
  const t = (text) => recipeComposerText(languageId, text);
  const value = Number(line.value || 0);
  if (!value || Number.isNaN(value)) return t("Set quantity");

  const defaults = { unit: "Main unit", subunit: "Subunit", subunit2: "Subunit 2" };
  const label = getUnitName(line[line.unit_level], languageId) || t(defaults[line.unit_level] || "unit");
  if (line.input_mode === "percent") {
    return `${formatRecipeNumber(value, 2, languageId)}% ${t("of")} ${label}`;
  }

  return `${formatRecipeNumber(value, 2, languageId)} ${label}`.trim();
}

function getFilteredItems(allItems, line) {
  return (allItems || []).filter((item) => {
    const hierarchy = deriveHierarchyFromItem(item);
    if (line.family_id && hierarchy.family_id !== String(line.family_id)) return false;
    if (line.subfamily_id && hierarchy.subfamily_id !== String(line.subfamily_id)) return false;
    if (line.category_id && hierarchy.category_id !== String(line.category_id)) return false;
    if (line.subcategory_id && hierarchy.subcategory_id !== String(line.subcategory_id)) return false;
    return true;
  });
}

function countItemsForSelection(allItems, selection) {
  return getFilteredItems(allItems, selection).length;
}

function buildPickerOption(row, field, allItems, selection) {
  const id = String(row?.id || "");
  return {
    id,
    field,
    name: row?.name || "Unnamed",
    count: countItemsForSelection(allItems, { ...selection, [field]: id }),
  };
}

function buildIngredientCatalog(allItems, families, picker) {
  const query = String(picker.query || "").trim();

  if (query) {
    return {
      mode: "search",
      title: "Search results",
      nodes: [],
      items: sortByName((allItems || []).filter((item) => matchesProductQuery(item, query))),
    };
  }

  if (!picker.family_id) {
    const familyRows = uniqueById([
      ...(families || []),
      ...(allItems || []).map((item) => getHierarchyObjectsFromItem(item).family).filter(Boolean),
    ]);

    return {
      mode: "families",
      title: "Families",
      nodes: sortByName(familyRows)
        .map((row) => buildPickerOption(row, "family_id", allItems, {}))
        .filter((node) => node.count > 0),
      items: [],
    };
  }

  const familySelection = { family_id: picker.family_id };

  if (!picker.subfamily_id) {
    const rows = uniqueById(
      getFilteredItems(allItems, familySelection)
        .map((item) => getHierarchyObjectsFromItem(item).subfamily)
        .filter(Boolean)
    );

    return {
      mode: "subfamilies",
      title: "Subfamilies",
      nodes: sortByName(rows)
        .map((row) => buildPickerOption(row, "subfamily_id", allItems, familySelection))
        .filter((node) => node.count > 0),
      items: [],
    };
  }

  const subfamilySelection = { ...familySelection, subfamily_id: picker.subfamily_id };

  if (!picker.category_id) {
    const rows = uniqueById(
      getFilteredItems(allItems, subfamilySelection)
        .map((item) => getHierarchyObjectsFromItem(item).category)
        .filter(Boolean)
    );

    return {
      mode: "categories",
      title: "Categories",
      nodes: sortByName(rows)
        .map((row) => buildPickerOption(row, "category_id", allItems, subfamilySelection))
        .filter((node) => node.count > 0),
      items: [],
    };
  }

  const categorySelection = { ...subfamilySelection, category_id: picker.category_id };
  const categoryItems = getFilteredItems(allItems, categorySelection);
  const subcategoryRows = uniqueById(
    categoryItems.map((item) => getHierarchyObjectsFromItem(item).subcategory).filter(Boolean)
  );

  if (!picker.subcategory_id && subcategoryRows.length) {
    return {
      mode: "subcategories",
      title: "Subcategories",
      nodes: sortByName(subcategoryRows)
        .map((row) => buildPickerOption(row, "subcategory_id", allItems, categorySelection))
        .filter((node) => node.count > 0),
      items: sortByName(categoryItems.filter((item) => !getHierarchyObjectsFromItem(item).subcategory)),
    };
  }

  const itemSelection = picker.subcategory_id
    ? { ...categorySelection, subcategory_id: picker.subcategory_id }
    : categorySelection;

  return {
    mode: "items",
    title: "Products",
    nodes: [],
    items: sortByName(getFilteredItems(allItems, itemSelection)),
  };
}

function findPickerLabel(allItems, families, field, id) {
  if (!id) return "";

  if (field === "family_id") {
    return (
      (families || []).find((family) => String(family.id) === String(id))?.name ||
      (allItems || [])
        .map((item) => getHierarchyObjectsFromItem(item).family)
        .find((row) => String(row?.id || "") === String(id))?.name ||
      ""
    );
  }

  const hierarchyKey = {
    subfamily_id: "subfamily",
    category_id: "category",
    subcategory_id: "subcategory",
  }[field];

  return (
    (allItems || [])
      .map((item) => getHierarchyObjectsFromItem(item)[hierarchyKey])
      .find((row) => String(row?.id || "") === String(id))?.name || ""
  );
}

function buildPickerTrail(allItems, families, picker) {
  return [
    { field: "family_id", label: findPickerLabel(allItems, families, "family_id", picker.family_id) },
    { field: "subfamily_id", label: findPickerLabel(allItems, families, "subfamily_id", picker.subfamily_id) },
    { field: "category_id", label: findPickerLabel(allItems, families, "category_id", picker.category_id) },
    {
      field: "subcategory_id",
      label: findPickerLabel(allItems, families, "subcategory_id", picker.subcategory_id),
    },
  ].filter((crumb) => crumb.label);
}

function buildNarrativePayload(form) {
  const narrative = serializeRecipeNarrative({
    description: form.description,
    preparation_steps: form.preparation_steps,
    internal_notes: form.internal_notes,
    gallery_urls: form.gallery_urls,
    yield_quantity: form.yield_quantity,
  });
  let previous = form.narrative_source;
  if (typeof previous === "string") { try { previous = JSON.parse(previous); } catch { previous = null; } }
  return previous && typeof previous === "object" && !Array.isArray(previous)
    ? JSON.stringify({ ...previous, ...JSON.parse(narrative || "{}") })
    : narrative;
}

export default function RecipeComposerModal({
  isOpen,
  mode = "create",
  recipeId = null,
  onClose,
  onSaved,
}) {
  const { themeId, languageId } = usePmsTheme();
  const t = (text) => recipeComposerText(languageId, text);
  const currentUser = readItemsFamiliesUser();
  const [businessUnitId] = useState(currentUser?.active_business_unit_id);
  const access = useCatalogAccess(businessUnitId);
  const isEditing = mode === "edit";
  const action = isEditing ? "purchasing.recipes.edit" : "purchasing.recipes.create";
  const required = useMemo(() => ["purchasing.recipes.view", action], [action]);
  const costPermissions = useMemo(() => [...required, "purchasing.recipes.costs.view"], [required]);
  const canEdit = access.can(required);
  const canViewCosts = access.can(costPermissions);
  const canManageManufacturing = access.can([...required, "purchasing.recipes.manage_manufacturing"]);
  const manufacturingSource = useRef({ isInventoriable: false, hasManufacturedItem: null, producedUnitId: "" });
  const [activeStep, setActiveStep] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [contentReady, setContentReady] = useState(false);
  const [families, setFamilies] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [equivalentProducts, setEquivalentProducts] = useState([]);
  const [equivalentsFailed, setEquivalentsFailed] = useState(false);
  const [photosBusy, setPhotosBusy] = useState(false);
  const photosBusyRef = useRef(false), photoSession = useRef({ committed: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(buildEmptyForm());
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false);
  const [ingredientPicker, setIngredientPicker] = useState(EMPTY_INGREDIENT_PICKER);
  const [selectedIngredientDetail, setSelectedIngredientDetail] = useState({
    loading: false,
    item: null,
    cost: null,
    error: "",
  });
  const [addingItemId, setAddingItemId] = useState("");
  const [dropActive, setDropActive] = useState(false);
  const [activeIngredientIndex, setActiveIngredientIndex] = useState(null);
  const saveInFlight = useRef(false);
  const openSession = useRef(0);
  const dialogRef = useItemDialogFocus(isOpen && canEdit, saving || photosBusy || Boolean(addingItemId), onClose);

  useEffect(() => {
    if (!isOpen || !canEdit) return undefined;
    let cancelled = false;
    const stamp = access.capture();
    const current = () => !cancelled && access.isCurrent(stamp, required);
    openSession.current += 1;

    const loadEverything = async () => {
      setContentReady(false);
      setLoadFailed(false);
      setActiveStep(0);
      photoSession.current.committed = false;
      setSaving(false);
      saveInFlight.current = false;
      setError("");
      setIngredientPickerOpen(false);
      setIngredientPicker(EMPTY_INGREDIENT_PICKER);
      setSelectedIngredientDetail({ loading: false, item: null, cost: null, error: "" });
      setAddingItemId("");
      setDropActive(false);
      setActiveIngredientIndex(null);

      try {
        const [familiesRes, itemsRes, unitsRes, equivalentsRes] = await Promise.all([
          axios.get("/api/purchasing/families", access.requestConfig(required)),
          axios.get("/api/purchasing/items", access.requestConfig(required)),
          axios.get("/api/purchasing/units", access.requestConfig(required)),
          axios.get("/api/purchasing/recipes/equivalent-products", access.requestConfig(required)).catch(() => null),
        ]);

        if (!current()) return;

        const sortedFamilies = sortByName(familiesRes.data || []);
        const sortedItems = sortByName(itemsRes.data || []);
        const sortedUnits = sortByName(unitsRes.data || []);

        setFamilies(sortedFamilies);
        setAllItems(sortedItems);
        setUnits(sortedUnits);
        setEquivalentProducts(equivalentsRes?.data?.groups || []);
        setEquivalentsFailed(!equivalentsRes);

        if (!isEditing || !recipeId) {
          manufacturingSource.current = { isInventoriable: false, hasManufacturedItem: null, producedUnitId: "" };
          setForm(buildEmptyForm());
          return;
        }

        const recipeRes = await axios.get(`/api/purchasing/recipes/${recipeId}`, access.requestConfig(required));
        if (!current()) return;

        const recipe = recipeRes.data || {};
        manufacturingSource.current = { isInventoriable: recipe.is_inventoriable === true, hasManufacturedItem: recipe.has_manufactured_item === true, producedUnitId: String(recipe.produced_unit_id || "") };
        const narrative = parseRecipeNarrative(recipe.technical_notes);

        const hydratedLines = await Promise.all(
          (recipe.lines || []).map(async (line) => {
            const catalogItem = sortedItems.find(
              (item) => String(item.id) === String(line?.item_id)
            );
            let unitCost = null;
            let fullItem = {};

            try {
              const [costRes, itemRes] = await Promise.all([
                canViewCosts ? axios.get(`/api/purchasing/items/cost/${line.item_id}`, access.requestConfig(costPermissions)) : Promise.resolve({ data: {} }),
                axios.get(`/api/purchasing/items/full/${line.item_id}`, access.requestConfig(required)),
              ]);
              unitCost = costRes.data?.cost ?? null;
              fullItem = itemRes.data || {};
            } catch (requestError) {
              console.warn("[RECIPES][COMPOSER][EDIT][LINE][LOAD]", requestError);
            }

            const mergedItem = mergeItemRecords({ ...(catalogItem || {}), ...(line.item || {}) }, fullItem);

            return buildLineFromItemRecord(mergedItem, unitCost, {
              item_id: line.item_id ? String(line.item_id) : "",
              item_name: line.item?.name || "",
              unit_level: line.unit_level || "unit",
              input_mode: line.input_mode || "quantity",
              value: line.value != null ? String(line.value) : "",
              is_confirmed: true,
            });
          })
        );

        if (!current()) return;
        setForm({
          name: recipe.name || "",
          code: recipe.code || "",
          recipe_type: recipe.recipe_type || "finished",
          photo_url: recipe.photo_url || "",
          is_inventoriable: Boolean(recipe.is_inventoriable),
          produced_unit_id: recipe.produced_unit_id ? String(recipe.produced_unit_id) : "",
          yield_quantity: narrative.yield_quantity,
          description: narrative.description || "",
          preparation_steps: narrative.preparation_steps || "",
          internal_notes: narrative.internal_notes || "",
          gallery_urls: narrative.gallery_urls.length ? narrative.gallery_urls : [""],
          narrative_source: recipe.technical_notes,
          lines: hydratedLines,
          equivalent_lines: recipe.equivalent_lines || [],
        });
      } catch (requestError) {
        console.error("[RECIPES][COMPOSER][LOAD][ERROR]", requestError);
        if (current()) {
          setLoadFailed(true);
          setError(
            isEditing
              ? "We could not load this recipe for editing."
              : "We could not prepare the recipe composer."
          );
        }
      } finally {
        if (current()) {
          setContentReady(true);
        }
      }
    };

    loadEverything();

    return () => {
      cancelled = true;
      openSession.current += 1;
    };
  }, [isEditing, isOpen, recipeId, loadAttempt, canEdit, access, required, costPermissions, canViewCosts]);

  const recipeTotal = useMemo(
    () =>
      form.lines.reduce((sum, line) => {
        if (line.total_cost != null) {
          return sum + Number(line.total_cost || 0);
        }
        const { computed_cost } = computeLineCost(line);
        return sum + Number(computed_cost || 0);
      }, 0),
    [form.lines]
  );
  const equivalentCost = form.equivalent_lines.reduce((sum, line) => {
    const product = equivalentProducts.find((row) => Number(row.id) === Number(line.equivalent_product_id));
    return sum === null || product?.currency !== "EUR" || product.current_unit_cost == null || !(Number(line.quantity) > 0) ? null : sum + Number(line.quantity) * Number(product.current_unit_cost);
  }, 0);
  const completeCost = equivalentCost === null || form.lines.some((line) => computeLineCost(line).computed_cost == null) ? null : recipeTotal + equivalentCost;

  const yieldQuantity = Number(form.yield_quantity || 0);
  const costPerYield =
    completeCost !== null && Number.isFinite(yieldQuantity) && yieldQuantity > 0 ? completeCost / yieldQuantity : null;

  const producedUnit = units.find(
    (unit) => String(unit.id) === String(form.produced_unit_id || "")
  );

  const ingredientCatalog = useMemo(
    () => buildIngredientCatalog(allItems, families, ingredientPicker),
    [allItems, families, ingredientPicker]
  );

  const pickerTrail = useMemo(
    () => buildPickerTrail(allItems, families, ingredientPicker),
    [allItems, families, ingredientPicker]
  );

  const selectedCatalogItem = selectedIngredientDetail.item ||
    allItems.find((item) => String(item.id) === String(ingredientPicker.selected_item_id || "")) ||
    null;

  useEffect(() => {
    if (!isOpen || !canEdit || !ingredientPicker.selected_item_id) {
      setSelectedIngredientDetail({ loading: false, item: null, cost: null, error: "" });
      return undefined;
    }

    let cancelled = false;
    const stamp = access.capture();
    const current = () => !cancelled && access.isCurrent(stamp, required);
    const itemId = ingredientPicker.selected_item_id;
    const catalogItem = allItems.find((item) => String(item.id) === String(itemId)) || {};

    setSelectedIngredientDetail({
      loading: true,
      item: catalogItem,
      cost: null,
      error: "",
    });

    const loadSelectedItem = async () => {
      try {
        const [itemRes, costRes] = await Promise.all([
          axios.get(`/api/purchasing/items/full/${itemId}`, access.requestConfig(required)),
          canViewCosts ? axios.get(`/api/purchasing/items/cost/${itemId}`, access.requestConfig(costPermissions)) : Promise.resolve({ data: {} }),
        ]);

        if (!current()) return;

        setSelectedIngredientDetail({
          loading: false,
          item: mergeItemRecords(catalogItem, itemRes.data || {}),
          cost: costRes.data?.cost == null ? null : Number(costRes.data.cost),
          error: "",
        });
      } catch (requestError) {
        console.warn("[RECIPES][COMPOSER][PICKER][DETAIL]", requestError);
        if (current()) {
          setSelectedIngredientDetail({
            loading: false,
            item: catalogItem,
            cost: null,
            error: "Detail unavailable",
          });
        }
      }
    };

    loadSelectedItem();

    return () => {
      cancelled = true;
    };
  }, [allItems, ingredientPicker.selected_item_id, isOpen, canEdit, canViewCosts, access, required, costPermissions]);

  if (!isOpen || !canEdit) return null;

  const handleClose = () => {
    if (saving || photosBusyRef.current || addingItemId) return;
    onClose?.();
  };

  const setLineValue = (index, changes) => {
    setForm((prev) => {
      const updated = [...prev.lines];
      updated[index] = { ...updated[index], ...changes };
      return { ...prev, lines: updated };
    });
  };

  const handleHeaderChange = (field, value) => {
    if (!access.can(required)) return;
    if (field === "is_inventoriable" && value && !manufacturingSource.current.hasManufacturedItem && !access.can("purchasing.items.create")) return;
    if (field === "produced_unit_id" && manufacturingSource.current.hasManufacturedItem) return;
    if ((field === "is_inventoriable" || (field === "produced_unit_id" && (form.is_inventoriable || manufacturingSource.current.isInventoriable || manufacturingSource.current.hasManufacturedItem))) && !access.can([...required, "purchasing.recipes.manage_manufacturing"])) return;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRecipeTypeChange = (value) => {
    if (!access.can(required) || (form.is_inventoriable && value !== form.recipe_type && !access.can([...required, "purchasing.recipes.manage_manufacturing"]))) return;
    setForm((prev) => ({
      ...prev,
      recipe_type: value,
    }));
  };

  const handleAddLine = () => {
    setActiveStep(1);
    setIngredientPickerOpen(true);
  };

  const handleMoveLine = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= form.lines.length || saving || addingItemId) return;
    setForm((previous) => {
      const lines = [...previous.lines];
      [lines[index], lines[next]] = [lines[next], lines[index]];
      return { ...previous, lines };
    });
    setActiveIngredientIndex((active) => active === index ? next : active === next ? index : active);
  };

  const handleRemoveLine = (index) => {
    if (saving || addingItemId) return;
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
    setActiveIngredientIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const buildRecipeLineFromItem = async (itemId, seed = {}) => {
    const catalogItem = allItems.find((item) => String(item.id) === String(itemId)) || {};
    const [costRes, itemRes] = await Promise.all([
      canViewCosts ? axios.get(`/api/purchasing/items/cost/${itemId}`, access.requestConfig(costPermissions)) : Promise.resolve({ data: {} }),
      axios.get(`/api/purchasing/items/full/${itemId}`, access.requestConfig(required)),
    ]);
    const mergedItem = mergeItemRecords(catalogItem, itemRes.data || {});
    return buildLineFromItemRecord(mergedItem, costRes.data?.cost ?? null, seed);
  };

  const handlePickerNodeSelect = (field, value) => {
    const resetMap = {
      family_id: { subfamily_id: "", category_id: "", subcategory_id: "" },
      subfamily_id: { category_id: "", subcategory_id: "" },
      category_id: { subcategory_id: "" },
      subcategory_id: {},
    };

    setIngredientPicker((prev) => ({
      ...prev,
      query: "",
      selected_item_id: "",
      [field]: String(value || ""),
      ...(resetMap[field] || {}),
    }));
  };

  const handlePickerReset = () => {
    setIngredientPicker(EMPTY_INGREDIENT_PICKER);
  };

  const handlePickerTrailSelect = (field) => {
    const resetMap = {
      family_id: { subfamily_id: "", category_id: "", subcategory_id: "", selected_item_id: "" },
      subfamily_id: { category_id: "", subcategory_id: "", selected_item_id: "" },
      category_id: { subcategory_id: "", selected_item_id: "" },
      subcategory_id: { selected_item_id: "" },
    };

    setIngredientPicker((prev) => ({
      ...prev,
      query: "",
      ...(resetMap[field] || {}),
    }));
  };

  const handleProductSelect = (itemId) => {
    setIngredientPicker((prev) => ({
      ...prev,
      selected_item_id: String(itemId || ""),
    }));
  };

  const handleAddProductToRecipe = async (itemId) => {
    if (!itemId || addingItemId || saving || !access.can(required)) return;
    const stamp = access.capture();
    const session = openSession.current;
    setAddingItemId(String(itemId));
    setError("");

    try {
      const nextLine = await buildRecipeLineFromItem(itemId);
      if (session !== openSession.current || !access.isCurrent(stamp, required)) return;
      setForm((prev) => ({
        ...prev,
        lines: [...prev.lines, nextLine],
      }));
      setActiveIngredientIndex(form.lines.length);
      handleProductSelect(itemId);
      setIngredientPickerOpen(true);
    } catch (requestError) {
      console.error("[RECIPES][COMPOSER][ADD][ITEM]", requestError);
      if (session === openSession.current && access.isCurrent(stamp, required)) setError("We could not add this ingredient with its current inventory cost.");
    } finally {
      if (session === openSession.current && access.isCurrent(stamp, required)) { setAddingItemId(""); setDropActive(false); }
    }
  };

  const handleIngredientDragStart = (event, item) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(INGREDIENT_DRAG_TYPE, String(item.id));
    event.dataTransfer.setData("text/plain", String(item.id));
    handleProductSelect(item.id);
  };

  const handleRecipeDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDropActive(true);
  };

  const handleRecipeDrop = (event) => {
    event.preventDefault();
    const itemId =
      event.dataTransfer.getData(INGREDIENT_DRAG_TYPE) || event.dataTransfer.getData("text/plain");
    setDropActive(false);
    handleAddProductToRecipe(itemId);
  };

  const handleEditLine = (index) => {
    setError("");
    setActiveIngredientIndex(index);
  };

  const handleConfirmLine = (index) => {
    const line = form.lines[index];
    const valueNumber = Number(line?.value || 0);

    if (!line?.item_id) {
      setError("Please select a product before confirming the ingredient.");
      return;
    }

    if (!Number.isFinite(valueNumber) || valueNumber <= 0) {
      setError("Please enter a quantity before confirming the ingredient.");
      setActiveIngredientIndex(index);
      return;
    }

    setError("");
    setLineValue(index, { is_confirmed: true });
    setActiveIngredientIndex(null);
  };

  const handleSave = async () => {
    if (!contentReady || loadFailed || !access.can(required) || saving || photosBusyRef.current || addingItemId || saveInFlight.current) return;
    setError("");

    const invalid = (message, step, fieldId) => {
      setError(message);
      setActiveStep(step);
      requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
    };

    if (!String(form.name || "").trim()) {
      invalid("Please enter a recipe name.", 0, "recipe_name");
      return;
    }

    if (!form.lines.length && !form.equivalent_lines.length) {
      setError("Please add at least one ingredient.");
      setActiveStep(1);
      setIngredientPickerOpen(true);
      return;
    }

    if (form.is_inventoriable && form.equivalent_lines.length) { setError("Generic ingredients are analytical only. Disable manufactured stock first."); setActiveStep(0); return; }
    if (form.equivalent_lines.some((line) => !line.equivalent_product_id || !Number.isFinite(Number(line.quantity)) || Number(line.quantity) <= 0)) { setError("Enter a positive quantity for every generic ingredient."); setActiveStep(1); return; }

    if (form.is_inventoriable && !String(form.code || "").trim()) {
      invalid("Inventoriable recipes need an internal code for the manufactured item.", 0, "recipe_code");
      return;
    }

    if (form.is_inventoriable && !form.produced_unit_id) {
      invalid("Inventoriable recipes need a produced unit.", 0, "recipe_produced_unit");
      return;
    }

    if (String(form.yield_quantity).trim() && (!Number.isFinite(Number(form.yield_quantity)) || Number(form.yield_quantity) <= 0)) {
      invalid("Please enter a yield greater than zero.", 0, "recipe_yield");
      return;
    }
    const invalidLine = form.lines.findIndex((line) => !line.item_id || !Number.isFinite(Number(line.value)) || Number(line.value) <= 0);
    if (invalidLine !== -1) {
      setActiveIngredientIndex(invalidLine);
      invalid("Please enter a valid quantity for every ingredient.", 1, `recipe-quantity-${invalidLine}`);
      return;
    }

    const payloadLines = form.lines.map((line) => ({
        item_id: line.item_id,
        unit_level: line.unit_level || "unit",
        input_mode: line.input_mode || "quantity",
        value: Number(line.value),
      }));

    const source = manufacturingSource.current;
    const changesManufacturing = Boolean(form.is_inventoriable) !== source.isInventoriable ||
      ((source.isInventoriable || source.hasManufacturedItem || form.is_inventoriable) && String(form.produced_unit_id || "") !== source.producedUnitId);
    const savePermissions = [...required, ...(changesManufacturing ? ["purchasing.recipes.manage_manufacturing"] : []), ...(form.is_inventoriable && !source.hasManufacturedItem ? ["purchasing.items.create"] : [])];
    if (!access.can(savePermissions)) return;
    const stamp = access.capture();
    saveInFlight.current = true;
    const session = openSession.current;
    setSaving(true);

    try {
      const payload = {
        name: String(form.name || "").trim(),
        code: String(form.code || "").trim() || null,
        recipe_type: form.recipe_type,
        is_inventoriable: Boolean(form.is_inventoriable),
        photo_url: String(form.photo_url || "").trim() || null,
        technical_notes: buildNarrativePayload(form) || null,
        produced_unit_id: form.produced_unit_id || null,
        lines: payloadLines,
        equivalent_lines: form.equivalent_lines.map((line) => ({ equivalent_product_id: Number(line.equivalent_product_id), quantity: Number(line.quantity) })),
      };

      const response = isEditing
        ? await axios.put(`/api/purchasing/recipes/${recipeId}`, payload, access.requestConfig(savePermissions))
        : await axios.post("/api/purchasing/recipes", payload, access.requestConfig(savePermissions));

      if (session !== openSession.current || !access.isCurrent(stamp, savePermissions)) return;
      photoSession.current.committed = true;
      // A refresh failure must not make a successful create look unsaved and invite a duplicate.
      try { await onSaved?.(response.data); } catch (refreshError) { console.warn("[RECIPES][COMPOSER][REFRESH]", refreshError); }
      if (session === openSession.current && access.isCurrent(stamp, savePermissions)) onClose?.();
    } catch (requestError) {
      console.error("[RECIPES][COMPOSER][SAVE][ERROR]", requestError);
      if (session !== openSession.current || !access.isCurrent(stamp, savePermissions)) return;
      setError(
        requestError?.response?.data?.error ||
          "We could not save this recipe. Please review the fields and try again."
      );
      setSaving(false);
      saveInFlight.current = false;
    }
  };

  return createPortal(<RecipeComposerView
    photoProps={{ canUpload: () => access.can(required), recipeId: isEditing ? recipeId : null, requestConfig: access.requestConfig(required), onChange: setForm, session: photoSession, onBusy: (value) => { photosBusyRef.current = value; setPhotosBusy(value); } }}
    canViewCosts={canViewCosts} canManageManufacturing={canManageManufacturing}
    canEnableManufacturing={Boolean(manufacturingSource.current.hasManufacturedItem) || access.can("purchasing.items.create")}
    producedUnitLocked={Boolean(manufacturingSource.current.hasManufacturedItem)}
    manufacturingLocked={!canManageManufacturing && (form.is_inventoriable || manufacturingSource.current.isInventoriable || Boolean(manufacturingSource.current.hasManufacturedItem))}
    t={t} themeId={themeId} languageId={languageId} dialogRef={dialogRef}
    contentReady={contentReady} loadFailed={loadFailed} retry={() => setLoadAttempt((attempt) => attempt + 1)}
    isEditing={isEditing} saving={saving || photosBusy} busy={saving || photosBusy || Boolean(addingItemId)} error={error}
    activeStep={activeStep} setActiveStep={setActiveStep} form={form} units={units}
    equivalentProducts={equivalentProducts} equivalentsFailed={equivalentsFailed}
    changeEquivalents={(lines) => setForm((previous) => ({ ...previous, equivalent_lines: lines }))}
    recipeTotal={completeCost} costPerYield={costPerYield} producedUnit={producedUnit}
    costIncomplete={completeCost === null}
    money={(value) => formatRecipeCurrency(value, languageId)} number={(value, digits) => formatRecipeNumber(value, digits, languageId)}
    ingredientPickerOpen={ingredientPickerOpen} ingredientPicker={ingredientPicker}
    ingredientCatalog={ingredientCatalog} pickerTrail={pickerTrail} selectedCatalogItem={selectedCatalogItem}
    selectedIngredientDetail={selectedIngredientDetail} addingItemId={addingItemId}
    dropActive={dropActive} activeIngredientIndex={activeIngredientIndex}
    handlers={{ close: handleClose, save: handleSave, header: handleHeaderChange, type: handleRecipeTypeChange,
      addLine: handleAddLine, removeLine: handleRemoveLine, editLine: handleEditLine, confirmLine: handleConfirmLine,
      line: setLineValue, moveLine: handleMoveLine, addProduct: handleAddProductToRecipe,
      dragStart: handleIngredientDragStart, dragOver: handleRecipeDragOver, dragLeave: () => setDropActive(false), drop: handleRecipeDrop,
      closeLibrary: () => setIngredientPickerOpen(false), search: (query) => setIngredientPicker((picker) => ({ ...picker, query, selected_item_id: '' })),
      resetPicker: handlePickerReset, node: handlePickerNodeSelect, trail: handlePickerTrailSelect, selectProduct: handleProductSelect }}
    helpers={{ computeLineCost, unitOptions: (line) => getLineUnitOptions(line, languageId), hierarchy: getHierarchyPathFromLine,
      quantitySummary: (line) => getLineQuantitySummary(line, languageId), pack: (line) => getItemPackSummary(line, languageId),
      itemPack: (item) => getItemPackSummary(item, languageId), supplier: (source) => t(getSupplierLabel(source)) }}
  />, document.body);
}

function computeLineCost(line) {
  const unitCost = Number(line.unit_cost);
  if (!line.item_id || line.unit_cost == null || !Number.isFinite(unitCost) || unitCost < 0) {
    return { quantity_main: null, computed_cost: null };
  }

  const valueNumber = Number(line.value || 0);
  if (!valueNumber || Number.isNaN(valueNumber) || valueNumber <= 0) {
    return { quantity_main: null, computed_cost: null };
  }

  const packSize = Number(line.pack_size) || null;
  const sub2Size = Number(line.subunit2_size) || null;

  let factorToMain = 1;

  if (line.unit_level === "subunit") {
    if (!packSize || packSize <= 0) {
      return { quantity_main: null, computed_cost: null };
    }
    factorToMain = 1 / packSize;
  } else if (line.unit_level === "subunit2") {
    if (!packSize || packSize <= 0 || !sub2Size || sub2Size <= 0) {
      return { quantity_main: null, computed_cost: null };
    }
    factorToMain = 1 / (packSize * sub2Size);
  }

  const multiplier = line.input_mode === "percent" ? valueNumber / 100 : valueNumber;
  const quantityMain = multiplier * factorToMain;
  const cost = quantityMain * unitCost;

  return {
    quantity_main: quantityMain,
    computed_cost: cost,
  };
}
